import React, { useEffect } from 'react';
import { Form, Input, Card, Row, Col, Button } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { notEmptyRule } from './validationUtils';
import ImageDisplayField from '../ImageDisplayField';
import SimpleEditor from 'components/RichTextEditor/SimpleEditor';

const { TextArea } = Input;

const TransportSection = ({ 
  sectionKey, 
  sectionData, 
  uploadingImage, 
  uploadProps, 
  handleImageUploadForSection,
  currentLanguage,
  form
}) => {
  const { t } = useTranslation();
  
  // Create dynamic field names based on current language
  const langKey = currentLanguage.toLowerCase();
  const getFieldName = (field) => ['content', langKey, sectionKey, field];

  // Define default structure for transport selects
  const defaultTransportSelect = {
    icon: '',
    icon_active: '',
    title: '',
    tabs: [
      {
        title: '',
        button: '',
        description: ''
      }
    ]
  };

  useEffect(() => {
    const selectsFieldName = getFieldName('selects');
    const currentSelects = form.getFieldValue(selectsFieldName);
    
    if (!currentSelects || currentSelects.length === 0) {
      const initialSelects = sectionData?.selects?.length > 0
        ? sectionData.selects
        : [defaultTransportSelect];

      form.setFieldsValue({
        [selectsFieldName.join('.')]: initialSelects
      });
    }
  }, [sectionData]);

  return (
    <>
      {/* Main Transport Section Title and Description */}

        <Col span={12}>
          <Form.Item
            name={getFieldName('title')}
            label={t('Titolo Sezione Trasporto')}
            rules={[notEmptyRule(t, 'Il campo non può essere vuoto')]}
          >
            <Input placeholder={t('Inserisci il titolo della sezione')} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={getFieldName('description')}
            label={t('Descrizione Sezione Trasporto')}
            rules={[notEmptyRule(t, 'Il campo non può essere vuoto')]}
          >
            <TextArea rows={2} placeholder={t('Inserisci la descrizione della sezione')} />
          </Form.Item>
        </Col>
  

      {/* Transport Selects */}
      <Form.Item
        name={getFieldName('selects')}
        label={t('Opzioni di Trasporto')}
      >

        
        <Form.List name={getFieldName('selects')}>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }, selectIndex) => (
                <Card
                  key={key}
                  size='small'
                  style={{ marginBottom: 16 }}
                  title={`${t('Opzione di Trasporto')} ${selectIndex + 1}`}
                  extra={
                    fields.length > 1 && (
                      <Button
                        type='text'
                        danger
                        icon={<DeleteOutlined />}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          remove(name);
                        }}
                        size='small'
                      >
                        {t('Rimuovi')}
                      </Button>
                    )
                  }
                >
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        {...restField}
                        name={[name, 'title']}
                        label={t('Titolo Opzione')}
                        rules={[notEmptyRule(t, 'Il campo non può essere vuoto')]}
                      >
                        <Input placeholder={t('Inserisci il titolo dell\'opzione')} />
                      </Form.Item>
                    </Col>
                  </Row>
                    <Row>

                  

                      <Form.Item
                        {...restField}
                        name={[name, 'icon']}
                        label={t('Icona')}
                        rules={[notEmptyRule(t, 'Il campo non può essere vuoto')]}
                      >
                        <ImageDisplayField
                          name={[...getFieldName('selects'), selectIndex, 'icon']}
                          label={t('Icona')}
                          uploadingImage={uploadingImage}
                          uploadProps={uploadProps}
                          onUpload={(file) => handleImageUploadForSection(file, sectionKey, selectIndex, 'icon')}
                          onRemove={() => {
                            const selectsPath = getFieldName('selects');
                            const selects = form.getFieldValue(selectsPath) || [];
                            
                            const newSelects = [...selects];
                            if (newSelects[selectIndex]) {
                              newSelects[selectIndex] = {
                                ...newSelects[selectIndex],
                                icon: ''
                              };
                              form.setFieldsValue({
                                content: {
                                  ...form.getFieldValue('content'),
                                  [currentLanguage.toLowerCase()]: {
                                    ...form.getFieldValue(['content', currentLanguage.toLowerCase()]),
                                    [sectionKey]: {
                                      ...form.getFieldValue(['content', currentLanguage.toLowerCase(), sectionKey]),
                                      selects: newSelects
                                    }
                                  }
                                }
                              });
                            }
                          }}
                        />
                      </Form.Item>
                      <Col span={12}>
                      </Col>

                      <Form.Item
                        {...restField}
                        name={[name, 'icon_active']}
                        label={t('Icona Attiva')}
                        rules={[notEmptyRule(t, 'Il campo non può essere vuoto')]}
                      >
                        <ImageDisplayField
                          name={[...getFieldName('selects'), selectIndex, 'icon_active']}
                          label={t('Icona Attiva')}
                          uploadingImage={uploadingImage}
                          uploadProps={uploadProps}
                          onUpload={(file) => handleImageUploadForSection(file, sectionKey, selectIndex, 'icon_active')}
                          onRemove={() => {
                            const selectsPath = getFieldName('selects');
                            const selects = form.getFieldValue(selectsPath) || [];
                            
                            const newSelects = [...selects];
                            if (newSelects[selectIndex]) {
                              newSelects[selectIndex] = {
                                ...newSelects[selectIndex],
                                icon_active: ''
                              };
                              form.setFieldsValue({
                                content: {
                                  ...form.getFieldValue('content'),
                                  [currentLanguage.toLowerCase()]: {
                                    ...form.getFieldValue(['content', currentLanguage.toLowerCase()]),
                                    [sectionKey]: {
                                      ...form.getFieldValue(['content', currentLanguage.toLowerCase(), sectionKey]),
                                      selects: newSelects
                                    }
                                  }
                                }
                              });
                            }
                          }}
                        />
                      </Form.Item>


                    </Row>

                  {/* Tabs for this transport option */}
                  <Form.List name={[name, 'tabs']}>
                    {(tabFields, { add: addTab, remove: removeTab }) => (
                      <>
                        <div style={{ marginBottom: 8 }}>
                          <Button
                            type='dashed'
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              addTab({
                                title: '',
                                button: '',
                                description: ''
                              });
                            }}
                            icon={<PlusOutlined />}
                            size='small'
                          >
                            {t('Aggiungi Tab')}
                          </Button>
                        </div>
                        {tabFields.map(({ key: tabKey, name: tabName, ...restTabField }, tabIndex) => (
                          <Card
                            key={tabKey}
                            size='small'
                            style={{ marginBottom: 8 }}
                            title={`${t('Tab')} ${tabIndex + 1}`}
                            extra={
                              tabFields.length > 1 && (
                                <Button
                                  type='text'
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    removeTab(tabName);
                                  }}
                                  size='small'
                                >
                                  {t('Rimuovi')}
                                </Button>
                              )
                            }
                          >
                            <Row gutter={16}>
                              <Col span={12}>
                                <Form.Item
                                  {...restTabField}
                                  name={[tabName, 'title']}
                                  label={t('Titolo')}
                                  rules={[notEmptyRule(t, 'Il campo non può essere vuoto')]}
                                >
                                  <Input placeholder={t('Inserisci il titolo')} />
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item
                                  {...restTabField}
                                  name={[tabName, 'button']}
                                  label={t('Testo del pulsante')}
                                  // rules={[notEmptyRule(t, 'Il campo non può essere vuoto')]}
                                >
                                  <Input placeholder={t('Inserisci il testo del pulsante')} />
                                </Form.Item>
                              </Col>
                            </Row>
                            <Row gutter={16}>
                              <Col span={24}>
                                <Form.Item
                                  {...restTabField}
                                  name={[tabName, 'description']}
                                  label={t('Descrizione')}
                                  rules={[notEmptyRule(t, 'Il campo non può essere vuoto')]}
                                >
                                  <SimpleEditor 
                                    rows={2} 
                                    placeholder={t('Inserisci la descrizione')} 
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          </Card>
                        ))}
                      </>
                    )}
                  </Form.List>
                </Card>
              ))}
              <Button
                type='dashed'
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  add(defaultTransportSelect);
                }}
                icon={<PlusOutlined />}
                style={{ width: '100%' }}
              >
                {t('Aggiungi Opzione di Trasporto')}
              </Button>
            </>
          )}
        </Form.List>
      </Form.Item>
    </>
  );
};

export default TransportSection;  